import { Component, OnInit, OnDestroy, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../providers/auth.service';
import { ProfileService } from '../../../providers/profile.service';
import { SubscriptionService } from '../../../providers/subscription.service';
import { ConfigService } from '../../../providers/config.service';
import { BvDialogService, BvSpinnerComponent, BvLogoLoaderComponent } from '../../../design-system';
import { CheckoutComponent } from '../../../subscription/checkout/checkout.component';
import { RoutingService } from '../../../providers/routing.service';
import { SignupComponent } from '../../signup/signup.component';
import { PricingUtils, PricingTier } from '../../utils/pricing-utils';
import { PricingCatalogService, PricingCatalogInfo } from '../../../providers/pricing-catalog.service';
import './PricingWebComponent';


type CountryInfoResponse = PricingCatalogInfo;

@Component({
    selector: 'app-pricing-web-component',
    template: `
    @if (profServ.sub_processing) {
      <div class="fixed inset-0 flex items-center justify-center" style="z-index: 10000; background: rgba(0,0,0,0.5);">
        <bv-logo-loader [size]="128"></bv-logo-loader>
      </div>
    }
    @if (catalogLoading) {
      <div class="flex items-center justify-center" style="min-height: 300px;">
        <bv-spinner size="lg"></bv-spinner>
      </div>
    }
    <div #container [style.display]="catalogLoading ? 'none' : 'block'"></div>
    `,
    styleUrls: [],
    imports: [BvSpinnerComponent, BvLogoLoaderComponent]
})
export class PricingWebComponentComponent implements OnInit, OnDestroy {
  @Input() context: 'marketing' | 'authenticated' = 'marketing';
  @Input() showToggles: boolean = true;
  @Input() showSavings: boolean = true;
  @Input() theme: 'dark' | 'light' = 'dark';
  
  @Output() planSelected = new EventEmitter<any>();
  @Output() intervalChanged = new EventEmitter<string>();

  private pricingComponent: any;
  private cachedCountryInfo: CountryInfoResponse | null = null;
  // Tracks which interval the web component is currently displaying. Drives
  // the per-interval currency override on the user's current-tier card: only
  // when this matches the user's actual sub interval do we show their pinned
  // sub-currency price. Switching to the other interval crosses the currency
  // boundary (would route through convertSubscriptionCurrency), so it shows
  // the geo price instead.
  private displayedInterval: 'monthly' | 'annually' = 'monthly';
  catalogLoading = true;

  constructor(
    private elementRef: ElementRef,
    private translate: TranslateService,
    private auth: AuthService,
    public profServ: ProfileService,
    private subServ: SubscriptionService,
    private config: ConfigService,
    private dialog: BvDialogService,
    private routing: RoutingService,
    private pricingCatalog: PricingCatalogService
  ) {}

  ngOnInit() {
    // For authenticated context, the parent gates this component on
    // profServ.latestProfile being available, so by the time we run here
    // ProfileService has set up its subscription Observable too (both happen
    // in the same auth.user handler). For marketing context, there's no
    // user / no subscription to wait for.
    const cached = this.pricingCatalog.getCached();
    if (cached) {
      this.cachedCountryInfo = cached;
      this.catalogLoading = false;
    }
    this.initializePricingComponent();
  }

  ngOnDestroy() {
    if (this.pricingComponent) {
      this.pricingComponent.removeEventListener('planSelect', this.handlePlanSelect.bind(this));
      this.pricingComponent.removeEventListener('intervalChange', this.handleIntervalChange.bind(this));
    }
  }

  private initializePricingComponent() {
    const container = this.elementRef.nativeElement.querySelector('div');

    // Create the web component
    this.pricingComponent = document.createElement('pricing-component');

    // Set properties
    this.pricingComponent.context = this.context;

    // Check for signup plan selection first, then fall back to profile settings
    const signupPlan = this.profServ.getSignupPlanSelection();

    // Map profile subInterval to pricing component interval format
    let profileInterval = this.profServ.latestProfile?.subInterval || 'monthly';
    if (profileInterval === 'annual') {
      profileInterval = 'annually';
    }

    this.displayedInterval = (signupPlan.interval || profileInterval) as
      | 'monthly'
      | 'annually';
    this.pricingComponent.interval = this.displayedInterval;
    this.pricingComponent.translations = this.getTranslations();

    // Set attributes
    this.pricingComponent.setAttribute('user-plan', this.getCurrentPlanIndex().toString());
    this.pricingComponent.setAttribute('user-interval', this.getCurrentPlanInterval());
    this.pricingComponent.setAttribute('show-toggles', this.showToggles.toString());
    this.pricingComponent.setAttribute('show-savings', this.showSavings.toString());
    this.pricingComponent.setAttribute('theme', this.theme);
    this.pricingComponent.setAttribute('locale', this.translate.currentLang || 'en');

    // If we have cached catalog data, apply tiers right away (no fallback flash).
    // Otherwise, leave tiers empty until the fetch returns; the wrapper template
    // shows a spinner overlay while catalogLoading is true.
    if (this.cachedCountryInfo) {
      this.applyTiers(this.cachedCountryInfo);
    }
    this.fetchAndApplyCatalogPricing();

    // Add event listeners (currencyChange listener removed — no toggle anymore)
    this.pricingComponent.addEventListener('planSelect', this.handlePlanSelect.bind(this));
    this.pricingComponent.addEventListener('intervalChange', this.handleIntervalChange.bind(this));

    // Listen for language changes and update translations + locale (for currency formatting)
    this.translate.onLangChange.subscribe((evt) => {
      this.pricingComponent.translations = this.getTranslations();
      this.pricingComponent.setAttribute('locale', evt.lang || 'en');
    });


    // Append to container
    container.appendChild(this.pricingComponent);
  }

  private async fetchAndApplyCatalogPricing() {
    try {
      const result = await this.pricingCatalog.get();
      if (!result || !result.tiers) {
        // Fetch failed and no cache. Fall back to USD defaults so the UI
        // doesn't stay stuck on the spinner forever.
        console.warn('[pricing] catalog unavailable; rendering USD defaults');
        if (this.pricingComponent) {
          this.pricingComponent.tiers = PricingUtils.getDefaultConfig().tiers;
        }
        this.catalogLoading = false;
        return;
      }
      this.cachedCountryInfo = result;
      this.applyTiers(result);
      this.catalogLoading = false;
    } catch (err) {
      console.warn('[pricing] catalog fetch threw', err);
      this.catalogLoading = false;
    }
  }

  /**
   * Build the tier array from the catalog response, with one important
   * special-case: if the logged-in user is currently on a tier, that tier's
   * price card should reflect what they ACTUALLY pay (their grandfathered
   * Stripe subscription amount), not the new catalog price. Other tiers
   * show catalog prices since switching = current pricing.
   */
  private applyTiers(result: CountryInfoResponse) {
    const defaults = PricingUtils.getDefaultConfig().tiers;
    const sub = this.profServ.latestProfile && this.profServ.latestProfile.subscription;
    const subCode = (sub && sub.code) || null; // "HOME_GAME" or "CLUB"
    const subPlan = sub && sub.plan;
    const subAmount = subPlan && subPlan.amount ? subPlan.amount / 100 : 0;
    const subCurrency = subPlan && subPlan.currency ? subPlan.currency.toUpperCase() : '';
    const subInterval = subPlan && subPlan.interval; // "month" or "year"

    // Per French Consumer Code Article L112-1, prices shown to EU consumers
    // must be VAT-inclusive (TTC). The catalog stores HT amounts; multiply
    // by (1 + vatRate/100) before display. Non-EU countries return vatRate=0
    // and this is a no-op.
    const vatRate = result.vatRate || 0;
    const vatMul = vatRate ? 1 + vatRate / 100 : 1;
    const ttc = (n: number) => Math.round(n * vatMul * 100) / 100;

    // Profiles flagged as for-profit operators (set from BVAdmin) get Pro
    // highlighted instead of Club.
    const isCommercialOperator = !!(this.profServ.latestProfile && (this.profServ.latestProfile as any).commercialOperator);

    const tiers: PricingTier[] = defaults.map((d) => {
      // Tier id → catalog productCode
      const productCode =
        d.id === 'home-game' ? 'home_game'
        : d.id === 'club' ? 'club'
        : d.id === 'pro' ? 'pro'
        : null;
      let updated: PricingTier = d;
      if (productCode) {
        const t = result.tiers[productCode];
        if (t) {
          updated = {
            ...d,
            currency: t.currency || result.currency || d.currency,
            monthly: typeof t.monthly === 'number' ? ttc(t.monthly) : d.monthly,
            annually: typeof t.annual === 'number' ? ttc(t.annual) : d.annually,
          };
        }
      }

      // If this tier is the user's CURRENT plan AND the displayed toggle
      // matches their sub interval, overlay their actual Stripe-pinned price
      // (e.g. legacy €9.99 Club EUR). When the toggle is on the OTHER
      // interval, switching = a currency change → show geo price instead so
      // the displayed currency label matches the actual amount they'd pay
      // through convertSubscriptionCurrency.
      const isUsersTier = subCode && (
        (d.id === 'home-game' && subCode === 'HOME_GAME') ||
        (d.id === 'club' && subCode === 'CLUB') ||
        (d.id === 'pro' && subCode === 'PRO')
      );
      const subIntervalDisplay = subInterval === 'year' ? 'annually' : 'monthly';
      const showSubPrice = this.displayedInterval === subIntervalDisplay;
      if (isUsersTier && subAmount > 0 && subCurrency && showSubPrice) {
        if (subInterval === 'month') {
          updated = { ...updated, currency: subCurrency, monthly: ttc(subAmount) };
        } else if (subInterval === 'year') {
          updated = { ...updated, currency: subCurrency, annually: ttc(subAmount) };
        }
      }

      if (isCommercialOperator) {
        if (d.id === 'pro') updated = { ...updated, featured: true };
        else if (d.id === 'club') updated = { ...updated, featured: false };
      }

      return updated;
    });
    this.pricingComponent.tiers = tiers;
    this.pricingComponent.setAttribute('vat-rate', String(vatRate));
  }

  private getTranslations(): Record<string, string> {
    return {
      PRICING_SECTION_TITLE: this.translate.instant('PRICING_SECTION_TITLE') || 'Choose the right plan for you',
      PRICING_SECTION_SUBTITLE: this.translate.instant('PRICING_SECTION_SUBTITLE') || 'Start free and upgrade as your poker games grow',
      PRICING_SAVINGS_MESSAGE: this.translate.instant('PRICING_SAVINGS_MESSAGE') || 'Save {amount} vs monthly',
      MONTHLY_BILLING: this.translate.instant('MONTHLY_BILLING') || 'Monthly',
      ANNUAL_BILLING: this.translate.instant('ANNUAL_BILLING') || 'Annual',
      SIGNUP: this.translate.instant('SIGNUP') || 'Sign Up',
      YOUR_PLAN: this.translate.instant('YOUR_PLAN') || 'Your Plan',
      STAY_ON_THIS_PLAN: this.translate.instant('STAY_ON_THIS_PLAN') || 'Stay on this plan',
      UPGRADE: this.translate.instant('UPGRADE') || 'Upgrade',
      DOWNGRADE: this.translate.instant('DOWNGRADE') || 'Downgrade',
      SELECT: this.translate.instant('SELECT') || 'Select',
      FREE: this.translate.instant('FREE') || 'Free',
      ADMIN_CONTACT_US: this.translate.instant('ADMIN_CONTACT_US') || 'Contact Us',
      LEAGUES: this.translate.instant('LEAGUES') || 'Leagues',
      FREE_PLAN_DESCRIPTION: this.translate.instant('FREE_PLAN_DESCRIPTION') || 'Perfect for casual home games with friends',
      ADMIN_EVERYTHING_IN_KITCHEN_TABLE: this.translate.instant('ADMIN_EVERYTHING_IN_KITCHEN_TABLE') || 'Everything from Kitchen Table plan plus:',
      ADMIN_EVERYTHING_HOME_GAME: this.translate.instant('ADMIN_EVERYTHING_HOME_GAME') || 'Everything from the Home Game plan plus:',
      ADMIN_EVERYTHING_CLUB: this.translate.instant('ADMIN_EVERYTHING_CLUB') || 'Everything from the Club plan plus:',
      PRO_PRIORITY_SUPPORT: this.translate.instant('PRO_PRIORITY_SUPPORT') || 'Priority support',
      PRO_COMMERCIAL_LICENSE: this.translate.instant('PRO_COMMERCIAL_LICENSE') || 'Commercial-use license',
      PRO_FOR_OPERATORS: this.translate.instant('PRO_FOR_OPERATORS') || 'For casinos, card rooms, and pub leagues',
      INCL_TAX: this.translate.instant('INCL_TAX') || 'incl. tax',
      PRO_THERMAL_PRINTER_SEATS: this.translate.instant('PRO_THERMAL_PRINTER_SEATS') || 'Print seat tickets to thermal printer',
      BETA: this.translate.instant('BETA') || 'Beta',
      MAX_9: this.translate.instant('MAX_9') || 'Max 9 players',
      MAX_27: this.translate.instant('MAX_27') || 'Max 27 players',
      ADMIN_REAL_TIME: this.translate.instant('ADMIN_REAL_TIME') || 'Real time synchronization',
      ADMIN_BLIND_STRUCTURE_CALCULATOR: this.translate.instant('ADMIN_BLIND_STRUCTURE_CALCULATOR') || 'Blind structure calculator',
      TOURNAMENT_CLOCK: this.translate.instant('TOURNAMENT_CLOCK') || 'Tournament clock',
      ADMIN_PAYOUT_CALCULATOR: this.translate.instant('ADMIN_PAYOUT_CALCULATOR') || 'Payout calculator',
      ADMIN_SEATING: this.translate.instant('ADMIN_SEATING') || 'Seating management',
      ADMIN_AD_FREE: this.translate.instant('ADMIN_AD_FREE') || 'Ad free',
      ADMIN_UNLIMITED_PLAYERS: this.translate.instant('ADMIN_UNLIMITED_PLAYERS') || 'Unlimited players',
      ADMIN_BOUNTY_TOURNAMENTS: this.translate.instant('ADMIN_BOUNTY_TOURNAMENTS') || 'Bounty tournaments',
      ADMIN_CUSTOM_BACKGROUND: this.translate.instant('ADMIN_CUSTOM_BACKGROUND') || 'Custom background',
      ADMIN_YOUR_LOGO: this.translate.instant('ADMIN_YOUR_LOGO') || 'Your logo',
      ENHANCED_CHIPSET_MANAGEMENT: this.translate.instant('ENHANCED_CHIPSET_MANAGEMENT') || 'Enhanced chipset management',
      ADMIN_CREATE_LEAGUES: this.translate.instant('ADMIN_CREATE_LEAGUES') || 'Create leagues',
      ADMIN_DEFINE_LEAGUE_RULES: this.translate.instant('ADMIN_DEFINE_LEAGUE_RULES') || 'Define league rules',
      ADMIN_AUTOMATIC_LEAGUE_RANKINGS: this.translate.instant('ADMIN_AUTOMATIC_LEAGUE_RANKINGS') || 'Automatic league rankings',
    };
  }

  private getCurrentPlanIndex(): number {
    const sub = this.profServ.latestProfile && this.profServ.latestProfile.subscription;
    if (sub && sub.code) {
      return this.config.getPlanIndex(sub.code);
    }
    return 0;
  }

  private getCurrentPlanInterval(): 'monthly' | 'annually' {
    const sub = this.profServ.latestProfile && this.profServ.latestProfile.subscription;
    if (sub && sub.plan) {
      return sub.plan.interval === 'year' ? 'annually' : 'monthly';
    }
    return 'monthly';
  }

  private handlePlanSelect(event: CustomEvent) {
    const { planId, currency, interval } = event.detail;

    // Convert pricing component interval format to profile service format
    const profileInterval = interval === 'annually' ? 'annual' : interval;

    if (!this.auth.uid) {
      // Free-tier (Kitchen Table) signups have nothing to checkout, so let
      // the signup land on /lobby and skip the /subscription detour. The
      // T&C/Privacy interstitial fires on first profile load wherever they
      // land. Paid-tier signups still route to /subscription so checkout
      // continues with the chosen plan.
      if (planId !== 'kitchen-table') {
        this.auth.altRoute = "/subscription";
        this.profServ.subCurrency = currency;
        this.profServ.subInterval = profileInterval;
      }
      this.dialog.open(SignupComponent);
      return;
    }

    // Handle plan changes for authenticated users
    const planIndex = this.getPlanIndexFromId(planId);
    const currentPlanIndex = this.getCurrentPlanIndex();
    const currentInterval = this.getCurrentPlanInterval();

    // "Stay on this plan" — user clicked their current tier *and* interval.
    // Match on both: a Home Game Monthly user clicking Home Game Annual is a
    // legitimate interval change and must fall through to the change-plan
    // branch, not short-circuit here. Setting these timestamps stops the
    // onboarding guard from redirecting them back here on next navigation
    // and resets the 14-day prompt window.
    if (planIndex === currentPlanIndex && interval === currentInterval) {
      this.markPricingDismissed();
      this.routing.toLobby();
      this.planSelected.emit(event.detail);
      return;
    }

    if (planIndex === 0) {
      // Cancel subscription (only reached for paid users downgrading to KT)
      this.routing.openSub = true;
      this.subServ.cancelSubscription();
      this.markPricingDismissed();
      this.routing.toLobby();
    } else {
      // Change plan
      this.profServ.planIndex = planIndex;

      // Update currency and interval in profile for checkout
      if (this.profServ.latestProfile) {
        this.profServ.latestProfile.currency = currency;
        this.profServ.latestProfile.subInterval = profileInterval;
      }

      // Update profile in Firebase. Also mark the onboarding-seen flag and
      // stamp the 14-day prompt timestamp — they've engaged with pricing.
      this.profServ.profileAF.update({
        currency: currency,
        subInterval: profileInterval,
        onboardingPricingSeen: true,
        lastUpgradePromptShown: Date.now(),
      }).catch((e) => console.warn('[wrapper] profileAF.update rejected', e));

      const needsProration = this.needsProration(planIndex, profileInterval);

      if (needsProration) {
        this.subServ.prorate(planIndex).then(() => {
          this.dialog.open(CheckoutComponent, { panelClass: 'checkout-dialog-panel' });
        }).catch((e) => console.warn('[wrapper] prorate rejected', e));
      } else {
        this.dialog.open(CheckoutComponent, { panelClass: 'checkout-dialog-panel' });
      }
    }

    this.planSelected.emit(event.detail);
  }

  /**
   * Mark that the user has interacted with the pricing screen. Sets the
   * onboarding-seen flag (so the guard doesn't redirect them here again on
   * next /lobby visit) and stamps the 14-day prompt timestamp. Fire-and-forget;
   * profile updates don't need to block the route change.
   */
  private markPricingDismissed() {
    try {
      this.profServ.profileAF.update({
        onboardingPricingSeen: true,
        lastUpgradePromptShown: Date.now(),
      });
    } catch (e) {
      console.warn('[wrapper] markPricingDismissed write failed', e);
    }
  }

  private handleIntervalChange(event: CustomEvent) {
    const interval = event.detail.interval;

    this.displayedInterval = interval as 'monthly' | 'annually';
    // Re-apply tiers so the user's current-tier card flips between sub price
    // (when toggle matches their sub interval) and geo price (when not).
    if (this.cachedCountryInfo) this.applyTiers(this.cachedCountryInfo);

    // Convert pricing component interval format to profile service format
    const profileInterval = interval === 'annually' ? 'annual' : interval;

    if (this.auth.uid) {
      this.profServ.changeInterval(profileInterval);
    }

    this.intervalChanged.emit(interval);
  }

  private getPlanIndexFromId(planId: string): number {
    // Maps the pricing-web-component tier id (kebab-case) to its index in
    // /config/plans. Indexes 3 and 4 are legacy CASINO/ANTE entries — Pro
    // was appended at 5 (see add-pro-plan.js).
    const planMap: { [key: string]: number } = {
      'kitchen-table': 0,
      'home-game': 1,
      'club': 2,
      'pro': 5,
    };
    return planMap[planId] !== undefined ? planMap[planId] : 0;
  }

  private needsProration(planIndex: number, interval: string): boolean {
    const currentPlan = this.getCurrentPlanIndex();
    const currentInterval = this.getCurrentPlanInterval();
    
    return currentPlan !== 0 && (planIndex !== currentPlan || interval !== currentInterval);
  }
}
